import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { ReturnGenerationPreview as PreviewData } from '@/app/types/compliance';
import { format, parseISO } from 'date-fns';

interface Props {
  data: PreviewData | undefined;
  isLoading: boolean;
  isError: boolean;
}

const ReturnGenerationPreviewComponent: React.FC<Props> = ({
  data,
  isLoading,
  isError,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span>Loading preview...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load preview. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Click "Preview Generation" to see what tasks would be created.
      </div>
    );
  }

  const formatDate = (dateStr: string): string => {
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex gap-4">
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-success" />
          <span className="text-sm">
            <strong>{data.returns_created}</strong> would be created
          </span>
        </div>
        <div className="flex items-center gap-2">
          <XCircle className="h-4 w-4 text-warning" />
          <span className="text-sm">
            <strong>{data.returns_skipped}</strong> would be skipped
          </span>
        </div>
      </div>

      {/* Errors */}
      {data.errors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc pl-4">
              {data.errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Preview Table */}
      {data.details.length > 0 ? (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                <TableHead>Party</TableHead>
                <TableHead>Return Type</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Fee</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.details.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    {item.action === 'would_create' ? (
                      <Badge variant="default" className="bg-success">
                        Create
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        Skip
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{item.party}</TableCell>
                  <TableCell>{item.return_type}</TableCell>
                  <TableCell>{item.period}</TableCell>
                  <TableCell>{formatDate(item.due_date)}</TableCell>
                  <TableCell>
                    {item.fee_amount ? `₹${item.fee_amount}` : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          No tasks to generate for the selected date.
        </div>
      )}
    </div>
  );
};

export default ReturnGenerationPreviewComponent;
